import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { api } from '../api/client';

interface Cfop {
  codigo: string;
  descricao: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSelect: (cfop: Cfop) => void;
}

async function fetchCfops(q: string): Promise<Cfop[]> {
  const { data } = await api.get<Cfop[]>(`/cfop${q ? `?q=${encodeURIComponent(q)}` : ''}`);
  return data;
}

export default function CfopBuscaModal({ open, onClose, onSelect }: Props) {
  const [busca, setBusca] = useState('');
  const [lista, setLista] = useState<Cfop[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const termo = busca.trim();
    const handle = setTimeout(() => {
      setLoading(true);
      fetchCfops(termo)
        .then((data) => {
          setLista(data);
          setHighlight(0);
        })
        .catch(() => setLista([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [open, busca]);

  useEffect(() => {
    if (open) {
      setBusca('');
      setHighlight(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector<HTMLButtonElement>(`[data-idx="${highlight}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      setHighlight((h) => Math.min(h + 1, lista.length - 1));
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlight((h) => Math.max(h - 1, 0));
      e.preventDefault();
    } else if (e.key === 'Enter') {
      if (lista[highlight]) {
        onSelect(lista[highlight]);
        e.preventDefault();
      }
    } else if (e.key === 'Escape') {
      onClose();
    }
  }

  const mostrarAjuda = useMemo(() => busca.trim().length === 0 && lista.length > 0, [busca, lista]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 backdrop-blur-sm dark:bg-black/60"
      onClick={onClose}
    >
      <div
        className="mx-4 w-full max-w-xl rounded-xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-white/10 dark:bg-ink-900"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-semibold text-slate-950 dark:text-white">Buscar CFOP / natureza de operação</h3>
          <button
            type="button"
            className="rounded p-1 text-neutral-500 hover:bg-slate-100 hover:text-slate-950 dark:text-neutral-400 dark:hover:bg-white/5 dark:hover:text-white"
            onClick={onClose}
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-500" />
          <input
            ref={inputRef}
            type="search"
            className="field-input pl-9"
            placeholder="Ex: venda, remessa, devolução, 5101, 6102"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={onKeyDown}
            autoComplete="off"
          />
          {loading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-neutral-500">
              carregando...
            </div>
          )}
        </div>

        {mostrarAjuda && (
          <p className="mb-2 text-xs text-neutral-600 dark:text-neutral-400">
            Mostrando CFOPs mais comuns. Digite para buscar na tabela completa ({lista.length} itens visíveis).
          </p>
        )}

        <div
          ref={listRef}
          className="max-h-80 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200 dark:divide-white/5 dark:border-white/10"
        >
          {lista.length === 0 && !loading ? (
            <p className="py-8 text-center text-sm text-neutral-600 dark:text-neutral-400">
              {busca.trim().length === 0 ? 'Digite para buscar CFOPs' : `Nenhum CFOP encontrado para "${busca}"`}
            </p>
          ) : (
            lista.map((cf, i) => {
              const isHi = i === highlight;
              return (
                <button
                  key={cf.codigo}
                  type="button"
                  data-idx={i}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => onSelect(cf)}
                  className={`w-full px-3 py-2 text-left text-sm transition ${
                    isHi ? 'bg-emerald-50 text-slate-950 dark:bg-emerald-500/10 dark:text-white' : 'text-slate-700 hover:bg-slate-100 dark:text-neutral-200 dark:hover:bg-white/5'
                  }`}
                >
                  <span className="mono mr-3 font-semibold text-emerald-700 dark:text-emerald-400">{cf.codigo}</span>
                  <span>{cf.descricao}</span>
                </button>
              );
            })
          )}
        </div>

        <p className="mt-3 text-xs text-neutral-600 dark:text-neutral-400">
          Use setas para navegar, Enter para selecionar, Esc para fechar.
        </p>
      </div>
    </div>
  );
}
